import {
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonText,
} from '@ionic/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Virtuoso } from 'react-virtuoso';

import { checkmark } from 'ionicons/icons';

import { useModal } from '../../../hooks/UI/useModal';
import { useEnter } from '../../../hooks/Utils/useEnter';
import { Address as AddressType, Address_Defaults } from '../../../types/User/types';
import { provincias } from '../../../utils/provincias';
import { capitalizeEveryWord } from '../../../utils/string';
import Button from '../../UI/Button';
import FormItem from '../../UI/FormItem';
import Modal from '../../UI/Modal';
import Spinner from '../../UI/Spinner';
import './Address.scss';
import { AddressProps } from './types';

const Address: React.FC<AddressProps> = ({
  address,
  setAddress,
  onSubmit: submitForm,
  submitButtonText,
  isSubmitting,
  onProvinciaChange,
  localidades,
  isFetchingLocalidades = false,
  isLoading = false,
}) => {
  const { ref, handleEnter } = useEnter();
  const normalizedAddress: AddressType = { ...Address_Defaults, ...address };
  const { control, handleSubmit, errors, watch, setValue } = useForm<AddressType>({
    defaultValues: normalizedAddress,
  });

  const selectedProvincia = watch('provincia');
  const selectedLocalidad = watch('localidad');

  const { isOpen, openModal, closeModal } = useModal();
  const [localidadSearch, setLocalidadSearch] = useState<string>('');

  const onSubmit = async (values: AddressType) => {
    const mergedValues = { ...normalizedAddress, ...values };
    setAddress(mergedValues);
    await submitForm(mergedValues);
  };

  const fields: {
    name: keyof AddressType;
    label: string;
    type: 'text' | 'number' | 'select' | 'modal';
    transform?: (value: any) => any;
    rules?: any;
  }[] = [
    {
      name: 'direccion',
      label: 'Dirección',
      type: 'text',
      transform: capitalizeEveryWord,
      rules: { required: 'Por favor, ingrese la calle' },
    },
    {
      name: 'numero',
      label: 'Altura',
      type: 'number',
      rules: {
        required: 'Por favor, ingrese la altura',
        min: { value: 1, message: 'La altura debe ser mayor a 0' },
      },
    },
    {
      name: 'piso',
      label: 'Piso (opcional)',
      type: 'number',
      rules: { min: { value: 0, message: 'El piso no puede ser negativo' } },
    },
    {
      name: 'departamento',
      label: 'Depto. (opcional)',
      type: 'text',
      transform: (v: string) => v?.toUpperCase(),
    },
    {
      name: 'provincia',
      label: 'Provincia',
      type: 'select',
      rules: { required: 'Seleccione una provincia' },
    },
    {
      name: 'localidad',
      label: 'Localidad',
      type: 'modal',
      rules: { required: 'Seleccione una localidad' },
    },
  ];

  // Abrir modal de localidades
  const handleOpenLocalidadModal = () => {
    const current = localidades.find((loc) => loc.value === selectedLocalidad);
    setLocalidadSearch(current?.label || '');
    openModal(null);
  };

  // Filtrar localidades por búsqueda
  const filteredLocalidades = useMemo(() => {
    if (!localidadSearch.trim()) return localidades;
    const searchLower = localidadSearch.toLowerCase();
    return localidades.filter((loc) => loc.label.toLowerCase().includes(searchLower));
  }, [localidades, localidadSearch]);

  // Seleccionar localidad del modal
  const handleSelectLocalidad = (loc: { value: string; label: string }) => {
    setValue('localidad', loc.value);
    closeModal();
  };

  // Mostrar label de localidad seleccionada
  const selectedLocalidadLabel = useMemo(() => {
    if (!selectedLocalidad) return '';
    const found = localidades.find((loc) => loc.value === selectedLocalidad);
    return found?.label || '';
  }, [selectedLocalidad, localidades]);

  // Notificar cambio de provincia al padre (para fetch de localidades)
  useEffect(() => {
    if (selectedProvincia && onProvinciaChange) {
      onProvinciaChange(selectedProvincia);
    }
  }, [selectedProvincia, onProvinciaChange]);

  // ✅ SOLUCIÓN: Solo sincronizar provincia y localidad desde el padre
  // Esto es necesario solo para la carga inicial (edición de usuario existente)
  useEffect(() => {
    if (!address) return;

    // Solo sincronizar provincia si el form está vacío y el padre tiene valor
    if (!selectedProvincia && address.provincia) {
      setValue('provincia', address.provincia);
    }

    // Solo sincronizar localidad si el form está vacío y el padre tiene valor
    if (!selectedLocalidad && address.localidad) {
      setValue('localidad', address.localidad);
    }
  }, [address, selectedProvincia, selectedLocalidad, setValue]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} onKeyUp={handleEnter}>
        {fields.map((field) => (
          <FormItem
            key={field.name}
            name={field.name}
            control={control}
            errors={errors}
            rules={field.rules}
            loading={isLoading || (field.name === 'localidad' && isFetchingLocalidades)}
            render={({ value, onChange }) => {
              switch (field.type) {
                case 'text':
                case 'number':
                  return (
                    <IonItem>
                      <IonLabel position="floating">{field.label}</IonLabel>
                      <IonInput
                        value={value ?? ''}
                        onIonChange={(e) => onChange(e.detail.value ?? '')}
                        onIonBlur={() => {
                          if (field.transform && value) {
                            onChange(field.transform(value));
                          }
                        }}
                      />
                    </IonItem>
                  );
                case 'select':
                  return (
                    <IonItem>
                      <IonLabel>{field.label}</IonLabel>
                      <IonSelect
                        value={value}
                        placeholder={`Seleccione ${field.label.toLowerCase()}`}
                        onIonChange={(e) => onChange(e.detail.value)}
                        okText="Confirmar"
                        cancelText="Cancelar"
                      >
                        {provincias.map((prov) => (
                          <IonSelectOption key={prov} value={prov}>
                            {prov}
                          </IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>
                  );
                case 'modal':
                  return (
                    <IonItem onClick={handleOpenLocalidadModal}>
                      <IonLabel position="floating">{field.label}</IonLabel>
                      <IonInput
                        readonly
                        value={selectedLocalidadLabel}
                        disabled={!selectedProvincia || isFetchingLocalidades}
                      >
                        {isFetchingLocalidades && <Spinner />}
                      </IonInput>
                    </IonItem>
                  );
                default:
                  return <></>;
              }
            }}
          />
        ))}

        <Button ref={ref} type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Spinner color="light" /> : submitButtonText}
        </Button>
      </form>

      <Modal isOpen={isOpen} onClose={closeModal}>
        <div className="localidad-modal">
          <IonSearchbar
            value={localidadSearch}
            onIonInput={(e) =>
              setLocalidadSearch((e.target as HTMLIonSearchbarElement).value || '')
            }
            placeholder="Buscar localidad..."
            debounce={300}
          />

          {filteredLocalidades.length === 0 ? (
            <div className="ion-text-center ion-padding">
              <IonText color="medium">
                <p>
                  {localidadSearch
                    ? 'No se encontraron localidades con ese nombre'
                    : 'No hay localidades disponibles'}
                </p>
              </IonText>
            </div>
          ) : (
            <Virtuoso
              className="virtuoso-container"
              totalCount={filteredLocalidades.length}
              itemContent={(index) => {
                const loc = filteredLocalidades[index];
                const isSelected = selectedLocalidad === loc.value;
                return (
                  <IonItem
                    key={loc.value}
                    button
                    onClick={() => handleSelectLocalidad(loc)}
                  >
                    <IonLabel>{loc.label}</IonLabel>
                    {isSelected && (
                      <IonIcon icon={checkmark} color="success" slot="end" />
                    )}
                  </IonItem>
                );
              }}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default React.memo(Address);
